import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Truck, FileDown, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const iso = (d) => d.toISOString().slice(0, 10);

function RestockButton({ g, it, idx, i, onDone }) {
  if (!g.vehicle_id || !it.item_id) return null;
  const restock = async () => {
    try {
      await api.post("/checklists/restock", { vehicle_id: g.vehicle_id, item_id: it.item_id });
      toast.success(`${it.name}: segnato come reintegrato`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
      <span className="text-[11px] text-muted-foreground" data-testid={`stats-restock-info-${idx}-${i}`}>
        {it.restock ? `Reintegrato il ${new Date(it.restock.at).toLocaleString("it-IT")} da ${it.restock.by_name}` : ""}
      </span>
      <Button size="sm" variant="ghost" onClick={restock} data-testid={`stats-restock-${idx}-${i}`} className="h-7 text-xs text-emerald-700">
        <PackageCheck className="h-3.5 w-3.5 mr-1" /> Reintegrato
      </Button>
    </div>
  );
}

function VehicleStats({ g, idx, onReload }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`stats-vehicle-${idx}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="font-semibold flex items-center gap-2"><Truck className="h-4 w-4 text-[#D32F2F]" /> {g.vehicle_name}</p>
        <Badge variant="secondary">{g.checklists} check-list</Badge>
      </div>
      {g.items.length === 0 ? (
        <p className="text-sm text-emerald-700">Nessun materiale mancante nel periodo.</p>
      ) : (
        <div className="space-y-2">
          {g.items.slice(0, 15).map((it, i) => (
            <div key={it.name} data-testid={`stats-item-${idx}-${i}`}>
              <div className="flex justify-between gap-3 text-sm mb-1 leading-5"><span>{it.name}</span><span className="font-semibold tabular-nums">{it.count}× · {it.pct}%</span></div>
              <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-[#D32F2F]" style={{ width: `${it.pct}%` }} /></div>
              <RestockButton g={g} it={it} idx={idx} i={i} onDone={onReload} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ChecklistStats({ initialFrom, initialTo, kind = "vano" }) {
  const [range, setRange] = useState(() => ({ date_from: initialFrom || iso(new Date(Date.now() - 90 * 864e5)), date_to: initialTo || iso(new Date()) }));
  const [data, setData] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const params = { kind, ...Object.fromEntries(Object.entries(range).filter(([, v]) => v)) };
    api.get("/checklists/stats", { params }).then((r) => setData(r.data)).catch(() => setData([]));
  }, [range, tick, kind]);
  const set = (k, v) => setRange((p) => ({ ...p, [k]: v }));
  const downloadPdf = async () => {
    try {
      const params = { kind, ...Object.fromEntries(Object.entries(range).filter(([, v]) => v)) };
      const res = await api.get("/checklists/stats/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `riepilogo-materiali-${kind}-${range.date_from || "inizio"}_${range.date_to || "oggi"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile scaricare il PDF");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3 rounded-xl border border-border bg-card p-4">
        <div className="space-y-1"><Label>Dal</Label><Input type="date" data-testid="stats-date-from" value={range.date_from} onChange={(e) => set("date_from", e.target.value)} /></div>
        <div className="space-y-1"><Label>Al</Label><Input type="date" data-testid="stats-date-to" value={range.date_to} onChange={(e) => set("date_to", e.target.value)} /></div>
        <div className="flex items-end sm:col-start-4 sm:justify-end">
          <Button onClick={downloadPdf} data-testid="stats-pdf" className="rounded-full"><FileDown className="h-4 w-4 mr-1" /> Scarica PDF</Button>
        </div>
      </div>
      {data.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="stats-empty">Nessuna check-list nel periodo selezionato.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">{data.map((g, i) => <VehicleStats key={g.vehicle_id || "none"} g={g} idx={i} onReload={() => setTick((t) => t + 1)} />)}</div>
      )}
    </div>
  );
}
